import { apiFetch } from "./apiFetch";
import { getConfig } from "../config";
import type {
  AcquireLockResult,
  ClaimStuckRequestResult,
  HeartbeatResult,
  RequestAccessResult,
  RespondAction,
  RespondResult,
} from "../types/lock";

const SESSION_HEADER = "X-Form-Session-Id";

function lockPath(formId: string, suffix = ""): string {
  return `/employer-intake/${formId}/lock${suffix}`;
}

export const lockApi = {
  acquireLock: (formId: string, sessionId: string) =>
    apiFetch<AcquireLockResult>(lockPath(formId), {
      method: "POST",
      headers: { [SESSION_HEADER]: sessionId },
    }),

  releaseLock: (formId: string, sessionId: string) =>
    apiFetch<void>(lockPath(formId), {
      method: "DELETE",
      headers: { [SESSION_HEADER]: sessionId },
    }),

  /**
   * Fire-and-forget release for tab close. Uses `navigator.sendBeacon` so the
   * request survives page unload. Beacon strips Authorization headers, so the
   * session id rides in the body — server matches it against the lock entity
   * to verify the caller owns the lock before deleting.
   */
  releaseLockBeacon: (formId: string, sessionId: string): boolean => {
    const url = `${getConfig().API_BASE_URL}${lockPath(formId, "/release")}`;
    const body = new Blob([JSON.stringify({ sessionId })], { type: "application/json" });
    return navigator.sendBeacon(url, body);
  },

  heartbeat: (formId: string, sessionId: string) =>
    apiFetch<HeartbeatResult>(lockPath(formId, "/heartbeat"), {
      method: "POST",
      headers: { [SESSION_HEADER]: sessionId },
    }),

  requestAccess: (formId: string, sessionId: string) =>
    apiFetch<RequestAccessResult>(lockPath(formId, "/request"), {
      method: "POST",
      headers: { [SESSION_HEADER]: sessionId },
    }),

  respondToRequest: (formId: string, sessionId: string, action: RespondAction) =>
    apiFetch<RespondResult>(lockPath(formId, "/respond"), {
      method: "POST",
      headers: { [SESSION_HEADER]: sessionId },
      body: JSON.stringify({ action }),
    }),

  /**
   * Requester withdraws their pending request. Server matches the caller's email
   * (from JWT claims) against the stored requestAccessBy so other parties cannot
   * cancel someone else's pending request.
   */
  cancelRequest: (formId: string) =>
    apiFetch<{ cancelled: boolean }>(lockPath(formId, "/cancel"), { method: "POST" }),

  /**
   * Requester self-heal at countdown=0. Auto-grant normally rides on the holder's
   * heartbeat; if the holder disappeared (logout-beacon-dropped, crash, network
   * drop) nothing fires the transfer. The requester drives it here instead —
   * server tries AutoGrantIfExpired, then falls back to a plain acquire if the
   * lock TTL already expired. On success the server pushes requestResolved
   * Granted so the existing SignalR handler drives the state transition.
   */
  claimStuckRequest: (formId: string, sessionId: string) =>
    apiFetch<ClaimStuckRequestResult>(lockPath(formId, "/claim"), {
      method: "POST",
      headers: { [SESSION_HEADER]: sessionId },
    }),

  /** Adds the caller's SignalR connection to the form's group so they receive push events. */
  joinGroup: (formId: string, connectionId: string) =>
    apiFetch<void>(lockPath(formId, "/join"), {
      method: "POST",
      body: JSON.stringify({ connectionId }),
    }),
};
